import type { ViewComponent } from './viewTypes'

/**
 * AB#2520 — pricing a document line in the form, before the document is saved.
 *
 * The decisions live here rather than inside `LineItemsSlot` so they can be tested: this package has no
 * jsdom, so logic left inline in the component is unreachable by any test. That is not a hypothetical
 * concern — AB#2518's copyFrom shipped wired to the wrong slot and entirely dead, with passing tests.
 */

export type LivePricingSpec = NonNullable<ViewComponent['livePricing']>

/** What the host is asked for. The renderer knows nothing about pricing or transport. */
export interface LinePriceRequest {
    process: string
    /** The product reference as the row holds it — an id when productEntity is declared. */
    product: unknown
    quantity: number
    /** How the host turns `product` into the key pricing rules match on. Absent = it already is one. */
    productEntity?: string
    productKey?: string
    /** Dimensions whose value the document already holds literally: name -> value. */
    dimensions: Record<string, unknown>
    /**
     * Dimensions the host must resolve first: pricing-dimension name -> the reference to resolve.
     * Split out rather than mixed in so the host has nothing to guess and this stays testable.
     * AB#2633 — `fallbackKey` is read off the SAME record when `key` resolves blank (the save-time
     * stage does the identical fallback, so preview and save cannot disagree over a blank key).
     */
    resolveDimensions?: Record<string, { entity: string; key: string; fallbackKey?: string; id: unknown }>
    /** The date to price as at. Absent = today. */
    pricingDate?: unknown
}

/**
 * Host seam. Returns the unit price for the line, or undefined when it cannot be priced.
 *
 * Deliberately narrow: the host runs the process and pulls the number out of whatever the pricing
 * engine returned, so the shared renderer carries no pricing response shape. A host that wires nothing
 * (the designer preview) simply never prices.
 */
export type FetchLinePrice = (request: LinePriceRequest) => Promise<number | undefined>

const isBlank = (v: unknown): boolean =>
    v === null || v === undefined || (typeof v === 'string' && v.trim() === '')

/** A positive, finite quantity. "0" and "" are not quantities to price. */
export function readQuantity(value: unknown): number | undefined {
    if (typeof value === 'number') { return Number.isFinite(value) && value > 0 ? value : undefined }
    if (typeof value === 'string' && value.trim() !== '') {
        const n = Number(value)
        return Number.isFinite(n) && n > 0 ? n : undefined
    }
    return undefined
}

/**
 * Is this row ready to be priced? It needs a product AND a quantity — Ben's requirement is that the
 * price appears "after the line has a material and qty", so a half-filled row must not fire a call, and
 * must not show a price derived from a missing quantity.
 */
export function isPriceable(row: Record<string, unknown>, spec: LivePricingSpec): boolean {
    return !isBlank(row[spec.productField]) && readQuantity(row[spec.quantityField]) !== undefined
}

/**
 * May we write this price? Only if the field is blank, or still holds the value WE last put there.
 *
 * A price the user typed is theirs: a negotiated line is the whole reason `overwriteExisting: false`
 * exists on the save-time stage, and a preview that overwrote it would undo the negotiation on screen.
 * `lastAuto` is what this last wrote for the row, so re-pricing after a quantity change replaces our own
 * number rather than declining forever.
 */
export function isPriceReplaceable(current: unknown, lastAuto: unknown): boolean {
    if (isBlank(current)) { return true }
    if (lastAuto === undefined) { return false }
    // Compare numerically where both are numbers: the field round-trips through the input as a string,
    // so 12.5 and "12.50" are the same price and must not read as a user edit.
    const a = typeof current === 'string' ? Number(current) : current
    const b = typeof lastAuto === 'string' ? Number(lastAuto) : lastAuto
    if (typeof a === 'number' && typeof b === 'number' && Number.isFinite(a) && Number.isFinite(b)) {
        return a === b
    }
    return current === lastAuto
}

/**
 * The header dimensions to price with, split into the ones the document already holds literally and the
 * ones that name a reference the host must resolve to a code.
 *
 * Keyed by PRICING DIMENSION NAME, not by document field name. That distinction is the whole point: the
 * engine matches rules on `customer`, so sending a dimension called `customerAccountId` would not match
 * anything even if its value were the right code. Sending the raw id would fail the same way for the
 * other reason — which is exactly how AB#2524's third critical made every quote fall back to list price.
 *
 * Only declared dimensions are sent; the document carries plenty a pricing call has no business seeing.
 */
export function buildDimensions(
    document: Record<string, unknown> | undefined,
    spec: LivePricingSpec,
): { dimensions: Record<string, unknown>; resolveDimensions: Record<string, { entity: string; key: string; fallbackKey?: string; id: unknown }> } {
    const dimensions: Record<string, unknown> = {}
    const resolveDimensions: Record<string, { entity: string; key: string; fallbackKey?: string; id: unknown }> = {}

    for (const [dimension, source] of Object.entries(spec.dimensions ?? {})) {
        if (typeof source === 'string') {
            const value = document?.[source]
            // A blank dimension is not a dimension — sending "" would narrow nothing and match nothing.
            if (!isBlank(value)) { dimensions[dimension] = value }
            continue
        }
        const id = document?.[source.field]
        if (!isBlank(id)) {
            resolveDimensions[dimension] = {
                entity: source.entity,
                key: source.key,
                ...(source.fallbackKey ? { fallbackKey: source.fallbackKey } : {}),
                id,
            }
        }
    }
    return { dimensions, resolveDimensions }
}

/** Everything needed to ask the host, or null when this row is not ready. */
export function buildRequest(
    row: Record<string, unknown>,
    document: Record<string, unknown> | undefined,
    spec: LivePricingSpec,
): LinePriceRequest | null {
    if (!isPriceable(row, spec)) { return null }
    const { dimensions, resolveDimensions } = buildDimensions(document, spec)
    const rawDate = spec.pricingDateField ? document?.[spec.pricingDateField] : undefined
    const pricingDate = isBlank(rawDate) ? undefined : rawDate
    return {
        process: spec.process,
        product: row[spec.productField],
        quantity: readQuantity(row[spec.quantityField])!,
        ...(spec.productEntity ? { productEntity: spec.productEntity } : {}),
        ...(spec.productKey ? { productKey: spec.productKey } : {}),
        dimensions,
        ...(Object.keys(resolveDimensions).length > 0 ? { resolveDimensions } : {}),
        ...(pricingDate !== undefined ? { pricingDate } : {}),
    }
}
